import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Stack,
  IconButton,
  Popover,
} from "@mui/material";
import { Close, Edit, Check } from "@mui/icons-material";
import axios from "axios";
import { THEME_NAME_OPTIONS } from "../components/staticData";
import FullPageLoader from "../components/FullPageLoader";

const THEME_ROLES = [
  { label: "Published", value: "main" },
  { label: "Draft", value: "unpublished" },
  { label: "Trial", value: "demo" },
  { label: "Development", value: "development" },
];
export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [isAddingTheme, setIsAddingTheme] = useState(false);
  const [newThemeLabel, setNewThemeLabel] = useState("");
  const [editingTheme, setEditingTheme] = useState(null); // value of theme being edited
  const [editLabel, setEditLabel] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [themeToDelete, setThemeToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  
  const defaultSettings = {
    filters: {
      countries: [
        {
          label: "United Arab Emirates",
          value: "United Arab Emirates",
        },
        {
          label: "United Kingdom",
          value: "United Kingdom",
        },
        { label: "United States", value: "United States" },
        { label: "Pakistan", value: "Pakistan" },
        { label: "India", value: "India" },
        { label: "Afghanistan", value: "Afghanistan" },
      ],
      themeOptions: THEME_NAME_OPTIONS,
    },
    emailOptions: {
      welcome: {
        name: "Welcome Email",
        id: "welcome",
        supportedThemerole: ["main"],
      },
      trial: {
        name: "Trial email",
        id: "trial",
        supportedThemerole: ["demo"],
      },
    },
  };
  const saveSettings = async () => {
    try {
      setLoading(true);

      let response = await axios.put(
        `${process.env.REACT_APP_API_URL}/updatesettings`,
        {
          filters: settings.filters,
          emailOptions: settings.emailOptions,
        }
      );

      // optional toast/snackbar
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getsettings`
      );

      if (res.data?.success) setSettings(res.data.data || defaultSettings);
    } catch (err) {
      console.error("Settings error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  const generateValue = (label) =>
    label.toLowerCase().trim().replace(/\s+/g, "-");

  const startEdit = ({ label, value }) => {
    setEditingTheme(value);
    setEditLabel(label);
  };

  const saveEdit = (value) => {
    if (!editLabel.trim()) return;

    setSettings((prev) => {
      return {
        ...prev,
        filters: {
          ...prev.filters,
          themeOptions: prev?.filters?.themeOptions?.map((t) =>
            t.value === value ? { ...t, label: editLabel.trim() } : t
          ),
        },
      };
    });

    setEditingTheme(null); // removes focused TextField
    setEditLabel("");
  };

  const openDeleteConfirm = (event, value) => {
    setAnchorEl(event.currentTarget);
    setThemeToDelete(value);
  };

  const closeDeleteConfirm = () => {
    setAnchorEl(null);
    setThemeToDelete(null);
  };

  const confirmDelete = () => {
    deleteTheme(themeToDelete);
    closeDeleteConfirm();
  };

  const saveNewTheme = () => {
    if (!newThemeLabel.trim()) return;

    const value = generateValue(newThemeLabel);

    setSettings((prev) => {
      if (prev?.filters?.themeOptions.some((t) => t.value === value))
        return prev; // prevent duplicates

      return {
        ...prev,
        filters: {
          ...prev.filters,
          themeOptions: [
            ...prev?.filters?.themeOptions,
            { label: newThemeLabel.trim(), value },
          ],
        },
      };
    });

    setNewThemeLabel("");
    setIsAddingTheme(false);
  };

  const deleteTheme = (value) => {
    setSettings((prev) => {
      return {
        ...prev,
        filters: {
          ...prev.filters,
          themeOptions: prev?.filters?.themeOptions.filter(
            (t) => t.value !== value
          ),
        },
      };
    });
  };

  const toggleRole = (key, role) => {
    setSettings((prev) => {
      const email = prev.emailOptions[key];

      if (!email) return prev; // safety guard

      const roles = email.supportedThemerole || [];
      const updatedRoles = roles.includes(role)
        ? roles.filter((r) => r !== role)
        : [...roles, role];

      return {
        ...prev,
        emailOptions: {
          ...prev.emailOptions,
          [key]: {
            ...email,
            supportedThemerole: updatedRoles,
          },
        },
      };
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {loading && <FullPageLoader/>}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 1,
        }}
      >
        <Button
          size="small"
          variant="contained"
          onClick={saveSettings}
          disabled={loading || !settings}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row", maxHeight: "50vh" }}
        spacing={3}
      >
        <Card
          sx={{
            flex: 1,
          }}
        >
          <CardContent>
            <Typography fontSize={16} variant="h6" mb={1}>
              Theme Filters
            </Typography>
            <Stack
              spacing={1}
              sx={{
                maxHeight: "70vh",
                overflow: "auto",
                p: 1,
              }}
            >
              {settings?.filters?.themeOptions?.map(({ label, value }) => (
                <Box
                  key={value}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {/* LABEL / EDIT FIELD */}
                  {editingTheme === value ? (
                    <TextField
                      size="small"
                      value={editLabel}
                      autoFocus
                      onChange={(e) => setEditLabel(e.target.value)}
                      onBlur={() => {
                        setEditingTheme(null);
                        setEditLabel("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(value);
                        if (e.key === "Escape") {
                          setEditingTheme(null);
                          setEditLabel("");
                        }
                      }}
                    />
                  ) : (
                    <Typography className="typography">{label}</Typography>
                  )}

                  {/* ACTIONS */}
                  <Stack direction="row" spacing={0.5}>
                    {editingTheme === value ? (
                      <>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => saveEdit(value)}
                        >
                          <Check fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setEditingTheme(null);
                            setEditLabel("");
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => startEdit({ label, value })}
                        >
                          <Edit fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => openDeleteConfirm(e, value)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Stack>
                </Box>
              ))}

              {!isAddingTheme ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsAddingTheme(true)}
                  sx={{ alignSelf: "flex-end" }}
                >
                  + Add new
                </Button>
              ) : (
                <Stack direction="row">
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Theme name"
                    value={newThemeLabel}
                    onChange={(e) => setNewThemeLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveNewTheme();
                      if (e.key === "Escape") {
                        setIsAddingTheme(false);
                        setNewThemeLabel("");
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={saveNewTheme}
                  >
                    Add
                  </Button>
                </Stack>
              )}
            </Stack>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={closeDeleteConfirm}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ p: 2, maxWidth: 250 }}>
                <Typography fontSize={14}>Delete this theme?</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={closeDeleteConfirm}
                  >
                    No
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={confirmDelete}
                  >
                    Yes
                  </Button>
                </Stack>
              </Box>
            </Popover>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography fontSize={16} fontWeight={600} mb={2}>
              Email Template – Theme Role Support
            </Typography>

            <Stack
              spacing={3}
              sx={{
                maxHeight: "70vh",
                overflow: "auto",
                p: 1,
              }}
            >
              {Object.entries(settings?.emailOptions || {}).map(
                ([key, email]) => {
                  const { name, supportedThemerole } = email;
                  const selectedRoles = THEME_ROLES.filter((role) =>
                    supportedThemerole?.includes(role.value)
                  );

                  const unselectedRoles = THEME_ROLES.filter(
                    (role) => !supportedThemerole?.includes(role.value)
                  );
                  return (
                    <Box
                      key={key}
                      sx={{
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                      }}
                    >
                      <Typography fontWeight={500} mb={1}>
                        {name}
                      </Typography>

                      <Typography fontSize={12} color="text.secondary" mb={1.5}>
                        Select which theme roles this email template supports
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {[...selectedRoles, ...unselectedRoles].map(
                          ({ label, value }) => {
                            const isSelected =
                              supportedThemerole?.includes(value);

                            return (
                              <Box
                                key={value}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 20,
                                  border: "1px solid",
                                  borderColor: isSelected
                                    ? "primary.main"
                                    : "grey.300",
                                  bgcolor: isSelected
                                    ? "rgba(25, 118, 210, 0.08)" // subtle blue tint
                                    : "grey.50",
                                  fontSize: 12,
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    borderColor: isSelected
                                      ? "primary.dark"
                                      : "grey.400",
                                    bgcolor: isSelected
                                      ? "rgba(25, 118, 210, 0.12)"
                                      : "grey.100",
                                  },
                                }}
                              >
                                <Typography
                                  fontSize={12}
                                  lineHeight={1}
                                  fontWeight={500}
                                  color={
                                    isSelected ? "primary.main" : "grey.700"
                                  }
                                >
                                  {label}
                                </Typography>

                                <Box
                                  component="span"
                                  onClick={() => toggleRole(key, value)}
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: 16,
                                    lineHeight: 1,
                                    fontWeight: 400,
                                    width: 16,
                                    height: 16,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "50%",
                                    color: isSelected
                                      ? "#e02218ff" // Red for remove
                                      : "#1b851eff", // Green for add
                                    bgcolor: isSelected
                                      ? "#f89994ff" // Red for remove
                                      : "#b0ebb2ff", // Green for add
                                    transition: "all 0.2s ease",
                                    userSelect: "none",
                                    "&:hover": {
                                      transform: "scale(1.1)",
                                      bgcolor: isSelected
                                        ? "#f1b5b5ff" // Darker red on hover
                                        : "#8abe8dff", // Darker green on hover
                                    },
                                    "&:active": {
                                      transform: "scale(0.95)",
                                    },
                                  }}
                                >
                                  {isSelected ? "−" : "+"}
                                </Box>
                              </Box>
                            );
                          }
                        )}
                      </Stack>
                    </Box>
                  );
                }
              )}
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent></CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
